import { Store } from "@/types/store";

export type CurrencyCode =
  | "USD" // U.S. Dollar
  | "CAD" // Canadian Dollar
  | "EUR" // Euro
  | "GBP" // British Pound
  | "AUD" // Australian Dollar
  | "SAR" // Saudi Riyal
  | "AED" // UAE Dirham
  | "KWD" // Kuwaiti Dinar
  | "BHD" // Bahraini Dinar
  | "JOD" // Jordanian Dinar
  | "EGP" // Egyptian Pound
  | "QAR" // Qatari Riyal
  | "ZAR" // South African Rand
  | "XXX" // No currency
  | "XXM"; // Multiple currencies (non standard)

const DEFAULT_CURRENCY_CODE = "USD";
const DEFAULT_CURRENCY_SYMBOL = "$";
const DEFAULT_LOCALE = new Intl.Locale("en-US");

// Returns the number of minor units, since it looks like
// Intl.NumberFormat doesn't handle this automatically
const fractionDigits = (currencyCode: CurrencyCode): number => {
  const code = currencyCode as string;

  // Some currencies use 3 decimal places (/1000 minor units),
  if (["BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"].includes(code)) {
    return 3;
  }

  // In case we're ever using Japanese Yen or other zero-decimal currencies
  if (
    [
      "BIF",
      "BYR",
      "CLF",
      "DJF",
      "GNF",
      "ISK",
      "JPY",
      "KMF",
      "KRW",
      "PYG",
      "RWF",
      "UGX",
      "UYI",
      "VND",
      "VUV",
      "XAF",
      "XOF",
      "XPF",
    ].includes(code)
  ) {
    return 0;
  }

  return 2;
};

export const formatCurrency = (
  value: number,
  currencyCode?: CurrencyCode,
  currencySymbol?: string,
  locale: Intl.Locale = DEFAULT_LOCALE, //PREFFERRED_LOCALE in the future once most stores have locale set
) => {
  const sanitizedValue = typeof value === "number" && isFinite(value) ? value : 0;

  // This logic is biased towards en-US, we don't respect the browser's locale here.
  let formatter: Intl.NumberFormat;

  // If a currency code exists, use it for formatting
  if (currencyCode) {
    if (currencyCode === "XXM") {
      // No currency, return custom symbol with value
      // This is used when reporting data from multiple stores accross different
      // currencies until we convert everything to a single currency
      return `💱${sanitizedValue.toFixed(2)}`;
    }

    // Default currency is USD if none provided and
    // a currency symbol is not provided
    currencyCode = currencyCode || "USD";

    try {
      formatter = new Intl.NumberFormat(locale.baseName, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: fractionDigits(currencyCode),
      });

      return formatter.format(sanitizedValue);
    } catch {
      // If the currency code is incorrect or unsupported, fall back to symbol only
      return `${currencySymbol || "¤"}${sanitizedValue.toLocaleString(DEFAULT_LOCALE.baseName, {
        minimumFractionDigits: 2,
      })}`;
    }
  } else {
    // Otherwise, fall back to currency symbol only
    return `${currencySymbol || "¤"}${sanitizedValue.toLocaleString(DEFAULT_LOCALE.baseName, {
      minimumFractionDigits: 2,
    })}`;
  }
};

export const formatCurrencyWithNoDecimals = (
  value: number,
  currencyCode?: CurrencyCode,
  currencySymbol?: string,
  locale: Intl.Locale = DEFAULT_LOCALE,
) => {
  const sanitizedValue = typeof value === "number" && isFinite(value) ? value : 0;

  if (currencyCode) {
    if (currencyCode === "XXM") {
      return `💱${sanitizedValue.toLocaleString(locale.baseName, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })}`;
    }

    try {
      const formatter = new Intl.NumberFormat(locale.baseName, {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });

      return formatter.format(sanitizedValue);
    } catch {
      return `${currencySymbol || "¤"}${sanitizedValue.toLocaleString(DEFAULT_LOCALE.baseName, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })}`;
    }
  }

  return `${currencySymbol || "¤"}${sanitizedValue.toLocaleString(DEFAULT_LOCALE.baseName, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })}`;
};

/**
 * Formats a value as currency for CSV export, limiting to 2 significant figures
 */
export const formatCurrencyCSV = (
  value: number,
  currencyCode?: CurrencyCode,
  currencySymbol?: string,
  locale: Intl.Locale = DEFAULT_LOCALE,
) => {
  const sanitizedValue = typeof value === "number" && isFinite(value) ? value : 0;

  try {
    const formatter = new Intl.NumberFormat(locale.baseName, {
      style: "currency",
      currency: currencyCode || "USD",
      maximumSignificantDigits: 2,
    });
    return formatter.format(sanitizedValue);
  } catch {
    return `${currencySymbol || "$"}${sanitizedValue.toLocaleString(locale.baseName, {
      maximumSignificantDigits: 2,
    })}`;
  }
};

export const formatCurrencyInStore = (
  value: number,
  store?: Store | null,
  locale: Intl.Locale = DEFAULT_LOCALE, //PREFFERRED_LOCALE in the future once most stores have locale set
) => {
  // If no currency symbol, prefer currency code or default to XXX
  const currencyCode = !store?.currencySymbol ? store?.currencyCode || "XXX" : store?.currencyCode;

  return formatCurrency(
    value,
    currencyCode as CurrencyCode,
    store?.currencySymbol || "XXX",
    locale,
  );
};

export const formatCurrencyInStoreWithNoDecimals = (
  value: number,
  store?: Store | null,
  locale: Intl.Locale = DEFAULT_LOCALE,
) => {
  const currencyCode = !store?.currencySymbol ? store?.currencyCode || "XXX" : store?.currencyCode;

  return formatCurrencyWithNoDecimals(
    value,
    currencyCode as CurrencyCode,
    store?.currencySymbol || "XXX",
    locale,
  );
};

export const getStoreCurrencyIdentifiers = (
  stores: Store[] | null | undefined,
): [string?, string?] => {
  if (!stores || stores.length === 0) {
    return [DEFAULT_CURRENCY_CODE, DEFAULT_CURRENCY_SYMBOL];
  }

  const uniqueSymbols = new Set(stores.map((store) => store.currencySymbol));
  const uniqueCodes = new Set(stores.map((store) => store.currencyCode));

  if (uniqueSymbols.size == 0 || uniqueCodes.size == 0) {
    return [DEFAULT_CURRENCY_CODE, DEFAULT_CURRENCY_SYMBOL];
  }

  if (uniqueSymbols.size >= 2 && uniqueCodes.size >= 2) {
    return ["XXM", "💱"];
  }

  switch (true) {
    case uniqueSymbols.size === 0 || uniqueCodes.size === 0:
      return [DEFAULT_CURRENCY_CODE, DEFAULT_CURRENCY_SYMBOL];

    case uniqueSymbols.size >= 2 || uniqueCodes.size >= 2:
      return ["XXM", "💱"];
  }

  const [symbol] = uniqueSymbols;
  const [code] = uniqueCodes;

  return [code, symbol];
};
